import { useState } from 'react';
import { AsyncAutocompleteSource } from '../../../../../../../../../components/v2/form/AsyncAutocomplete/asyncAutocomplete.types';
import { useModalState } from '../../../../../../../../../hooks/useModalState';
import { useFindConsultants } from '../../../../../../../../../services/modules/consultants/consultants.queries';
import { useUpdateOrderGeneralDataMutation } from '../../../../../../../../../services/modules/order/order.mutations';
import { UpdateOrderGeneralDataParams } from '../../../../../../../../../services/modules/order/order.types';
import useGeneralSettingsStore from '../../../../../../../../../stores/GeneralSettings/useGeneralSettingsStore';
import { useApplicationContext } from '../../../../../../../../context/ApplicationContext/ApplicationContext';
import { isAdminTeceo } from '../../../../../../../../context/ApplicationContext/applicationContext.utils';
import { UsedInOfflineMode } from '../../../../../../../../offline/types/UsedInOfflineMode';
import { useOrderContext } from '../../../../../../../contexts/Order.context';
import { useOrderCalculateContext } from '../../../../../../../contexts/OrderCalculate.context';
import useCustomerStatusToCreateOrder from '../../../../../../../hooks/useCustomerStatusToCreateOrder';
import { Order } from '../../../../../../../order.types';
import { buildOrderGeneralDataOfflineFetchers } from '../orderGeneralDataOfflineFetchers.utils';
import { buildOrderGeneralDataPermissions } from '../orderGeneralDataPermissions';
import useOrderSellerSource from './useOrderSellerSource';

export interface OrderGeneralDataSources {
  customer: AsyncAutocompleteSource;
  seller: AsyncAutocompleteSource;
  salesRepresentative: AsyncAutocompleteSource;
  priceTable: AsyncAutocompleteSource;
  consultant: AsyncAutocompleteSource;
}

interface UseOrderObjectGeneralDataSectionParams extends UsedInOfflineMode {
  order: Order;
  isReadOnlyView: boolean;
}

const useOrderObjectGeneralDataSection = ({
  order,
  isReadOnlyView,
  isOfflineMode,
}: UseOrderObjectGeneralDataSectionParams) => {
  const { canEditOrder: canEditOrderStatus } = useOrderContext();
  const canEditOrder = canEditOrderStatus && !isReadOnlyView;
  const { calculateSummary } = useOrderCalculateContext();
  const { isAdminBrand, isSeller, isStore, state } = useApplicationContext();
  const { generalSettings } = useGeneralSettingsStore();
  const customerStatusToCreateOrder = useCustomerStatusToCreateOrder();
  const priceTableConflictModal = useModalState();
  const [lastSubmittedData, setLastSubmittedData] = useState<UpdateOrderGeneralDataParams>();

  const brandId = order.brand?.id;
  const customerId = order.customer?.id;
  const salesRepresentativeId = order.salesRepresentative?.id;

  const permissions = buildOrderGeneralDataPermissions({
    canEditOrder,
    isAdminBrand,
    isAdminTeceo: isAdminTeceo(),
    isSeller,
    isStore,
    enablePriceTableByRepresentative: generalSettings.enablePriceTableByRepresentative,
    blockOrderWithoutCustomer: generalSettings.blockOrderWithoutCustomer,
    orderSellerId: order.seller?.id,
    currentUserId: state.user?.userId,
    customerId,
    isOfflineMode,
  });

  const { data: consultantsData } = useFindConsultants(
    { active: true, limit: 1, skip: 0 },
    { enabled: !isOfflineMode },
  );
  const isConsultantVisible =
    !isOfflineMode && ((consultantsData?.count ?? 0) > 0 || Boolean(order.consultant));

  const { isPending, updateOrderGeneralData } = useUpdateOrderGeneralDataMutation({
    orderId: order.id,
    currentCustomerId: customerId,
    isOfflineMode,
    onSuccess: calculateSummary,
    onPriceTableConflict: priceTableConflictModal.openModal,
  });

  const submit = (data: UpdateOrderGeneralDataParams) => {
    setLastSubmittedData(data);
    updateOrderGeneralData(data);
  };

  const confirmPriceTableUpdate = () => {
    priceTableConflictModal.closeModal();

    if (lastSubmittedData) {
      updateOrderGeneralData({ ...lastSubmittedData, allowUpdatePriceTable: true });
    }
  };

  const customerSource: AsyncAutocompleteSource = {
    url: 'customers',
    queryField: 'query',
    limit: 20,
    order: { commercialName: 'ASC' },
    defaultQuery: { status: customerStatusToCreateOrder },
    enabled: permissions.canEditCustomer,
  };

  const offlineFetchers = buildOrderGeneralDataOfflineFetchers({
    isOfflineMode,
    isAdminBrand,
    customerId,
  });

  const { source: sellerSource, fetcher: sellerFetcher } = useOrderSellerSource({
    shouldFetchSellers: permissions.shouldFetchSellers,
    offlineFetcher: offlineFetchers?.seller,
  });

  const salesRepresentativeSource: AsyncAutocompleteSource = {
    url: isAdminBrand ? 'sales-representatives' : `customers/${customerId}/sales-representatives`,
    queryField: 'nameOrCode',
    limit: 10,
    order: { code: 'ASC' },
    defaultQuery: { brandId },
    enabled: permissions.canEditSalesRepresentative && (isAdminBrand || !!customerId),
  };

  const priceTableSource: AsyncAutocompleteSource = {
    url: permissions.shouldUseBrandPriceTables
      ? 'price-tables'
      : 'sales-representatives/order/available-price-tables',
    queryField: 'nameOrCode',
    limit: 30,
    order: { code: 'ASC', defaultTable: 'ASC' },
    defaultQuery: { brandId, salesRepresentativeId, active: true },
    enabled: permissions.canEditPriceTable,
  };

  const consultantSource: AsyncAutocompleteSource = {
    url: 'consultants',
    queryField: 'name',
    limit: 10,
    defaultQuery: { brandId, active: true },
    enabled: permissions.canEditConsultant,
  };

  const disableWhileSaving = (canEdit: boolean) => !canEdit || isPending;

  const disabledFields = {
    customer: disableWhileSaving(permissions.canEditCustomer),
    seller: disableWhileSaving(permissions.canEditSeller),
    salesRepresentative: disableWhileSaving(permissions.canEditSalesRepresentative),
    priceTable: disableWhileSaving(permissions.canEditPriceTable),
    consultant: disableWhileSaving(permissions.canEditConsultant),
  };

  return {
    canEditOrder,
    permissions,
    disabledFields,
    isConsultantVisible,
    isPending,
    sources: {
      customer: customerSource,
      seller: sellerSource,
      salesRepresentative: salesRepresentativeSource,
      priceTable: priceTableSource,
      consultant: consultantSource,
    } satisfies OrderGeneralDataSources,
    fetchers: { ...offlineFetchers, seller: sellerFetcher },
    priceTableConflictModal,
    confirmPriceTableUpdate,
    submit,
  };
};

export default useOrderObjectGeneralDataSection;
